import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import uuid from 'uuid/v4';

// @atlaskit
import { Grid, GridColumn } from '@atlaskit/page';
import Button, { ButtonGroup } from '@atlaskit/button';
import ExcelSpreadsheetIcon from '@atlaskit/icon-file-type/glyph/excel-spreadsheet/24';
import FieldText from '@atlaskit/field-text';
import Form, { Field, FormSection, FormFooter } from '@atlaskit/form';

// @source
import { crypto } from '../../../utils';
import { fields } from '../../../constants';
import { Messages, SchemaGenerator } from '../../components';
import { Report as API, File } from '../../../server';
import { Validation } from '../../../tools';
import * as Styled from './styled';
import Layout from '../../templates/Layout';

const initialState = {
  isLoading: 'fetch',
  message: false,
};

const initialStatic = {
  configuration: {},
  report: '',
  template: false,
};

class EditReport extends React.Component {
  constructor(props) {
    super(props);
    this.uuid = crypto.decode(this.props.match.params.uuid);
    this.state = initialState;
    this.static = initialStatic;
    this.unmount = false;
    this.handleDeleteTemplate = this.handleDeleteTemplate.bind(this);
    this.handleUploadTemplate = this.handleUploadTemplate.bind(this);
    this.onCloseMessage = this.onCloseMessage.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.setSchemaGeneratorConfig = this.setSchemaGeneratorConfig.bind(this);
  }

  componentWillMount() {
    API.detail({
      data: {
        report: this.uuid,
      },
      success: (data) => {
        this.setStatic({
          report: this.uuid,
          template: data.Template,
          configuration: {
            table: data.Table,
            rules: data.Configuration.map((item) => {
              const explode = item.TableAddress.split('$');
              return {
                uuid: uuid(),
                cell: item.Column,
                row: item.Row,
                column: explode[0] ? explode[1] : '',
                formula: !explode[0] ? explode[1] : '',
              };
            }),
          },
        });
        this.setStateUnmount({
          isLoading: '',
        });
      },
      error: (messages) => {
        this.setStateUnmount({
          isLoading: '',
          message: (
            <Messages
              items={messages}
              onClose={this.onCloseMessage}
            />
          ),
        });
      },
    });
  }

  componentWillUnmount() {
    this.unmount = true;
  }

  onCloseMessage() {
    this.setState({ message: false });
  }

  setStateUnmount(state) {
    if (this.unmount) return;
    this.setState(state);
  }

  setStatic(state) {
    this.static = { ...this.static, ...state };
  }

  setSchemaGeneratorConfig(output) {
    this.setStatic({ configuration: output });
  }

  handleSubmit(event) {
    event.preventDefault();
    this.setState({
      isLoading: 'update',
      message: false,
    });

    API.update({
      data: {
        uuid: this.uuid,
        report: this.static.report,
        configuration: this.static.configuration,
      },
      dispatch: this.props.dispatch,
      success: (messages) => {
        this.setStateUnmount({
          isLoading: '',
          message: (
            <Messages
              appearance="success"
              items={messages}
              onClose={this.onCloseMessage}
            />
          ),
        });
      },
      error: (messages) => {
        this.setStateUnmount({
          isLoading: '',
          message: (
            <Messages
              items={messages}
              onClose={this.onCloseMessage}
            />
          ),
        });
      },
    });
  }

  handleDeleteTemplate() {
    this.setState({ isLoading: 'remove' });

    API.removeTemplate({
      data: {
        report: this.static.report,
      },
      success: () => {
        this.setStatic({ template: '' });
        this.setState({ isLoading: '' });
      },
    });
  }

  handleUploadTemplate() {
    const template = `${this.props.account.Company}_${this.static.report}.xlsx`;

    this.setState({ isLoading: 'upload' });

    File.upload({
      data: {
        params: `reportname=${template}`,
        files: this.template.files[0],
      },
      success: () => {
        this.setStatic({ template });
        this.setState({ isLoading: '' });
      },
    });
  }

  renderReportName() {
    return (
      <FormSection
        title="Report Name"
        description="If you type the name of the report that you want to change, the user permissions remain the same."
      >
        <Field
          {...fields.defaults.field}
          name="report"
          validators={[Validation.required]}
        >
          <FieldText
            {...fields.defaults.fieldText}
            onChange={event => this.setStatic({ report: event.target.value })}
            value={this.static.report}
          />
        </Field>
      </FormSection>
    );
  }

  renderConfiguration() {
    return (
      <FormSection
        title="Configuration"
        description="Set report configurations. The report output will be generated according to all the settings you have made."
      >
        <SchemaGenerator
          schema={this.static.configuration}
          output={this.setSchemaGeneratorConfig}
        />
      </FormSection>
    );
  }

  renderUploadTemplate() {
    const { isLoading } = this.state;
    let filename = '';

    if (this.template && this.template.files.length) {
      const { name } = this.template.files[0];
      filename = name;
    }

    return (
      <Styled.UploadFile>
        <div style={{ flex: 1 }}>
          <Styled.FileDragArea>
            <input
              type="file"
              accept=".xlsx"
              ref={(node) => { this.template = node; }}
              onChange={() => this.setState({})}
            />
            <ExcelSpreadsheetIcon />
            <span style={{ marginLeft: '.75rem' }}>{filename || 'Select a file'}</span>
          </Styled.FileDragArea>
        </div>
        <div style={{ padding: '0 1rem' }}>
          <Button
            appearance="primary"
            isDisabled={!filename}
            onClick={this.handleUploadTemplate}
            isLoading={isLoading === 'upload'}
          >
            Upload template
          </Button>
        </div>
      </Styled.UploadFile>
    );
  }

  renderTemplate() {
    const { isLoading } = this.state;
    const { template } = this.static;

    return (
      <FormSection
        title="Template"
        description="Please select the template you want to upload, this template will be used in the report."
      >
        {template ? (
          <Styled.UploadFile>
            <div style={{ flex: 1 }}>
              <Styled.FileDragArea>
                <ExcelSpreadsheetIcon />
                <span style={{ marginLeft: '.75rem' }}>{template}</span>
              </Styled.FileDragArea>
            </div>
            <div style={{ padding: '0 1rem' }}>
              <Button
                appearance="danger"
                isLoading={isLoading === 'remove'}
                onClick={this.handleDeleteTemplate}
              >
                Delete template
              </Button>
            </div>
          </Styled.UploadFile>
        ) : this.renderUploadTemplate()}
      </FormSection>
    );
  }

  renderFormFooter() {
    return (
      <FormFooter>
        <ButtonGroup>
          <Button
            appearance="primary"
            isLoading={this.state.isLoading === 'update'}
            onClick={this.handleSubmit}
            type="submit"
          >
            Update report
          </Button>
        </ButtonGroup>
        {this.state.message}
      </FormFooter>
    );
  }

  render() {
    return (
      <Layout isLoading={this.state.isLoading === 'fetch'}>
        <Grid>
          <GridColumn>
            <Form
              name="edit-report"
              onSubmit={this.handleSubmit}
              ref={(node) => { this.form = node; }}
            >
              {this.renderReportName()}
              {this.renderConfiguration()}
              {this.renderTemplate()}
              {this.renderFormFooter()}
            </Form>
          </GridColumn>
        </Grid>
      </Layout>
    );
  }
}

EditReport.propTypes = {
  account: PropTypes.shape().isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default connect()(EditReport);
